import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";
import { useNavigate, useParams } from "react-router";
import styled from "styled-components";
import axios from "axios";
import { API_BASE_URL, API_URLS, LOCALSTORAGE_TOKEN_ITEM } from "constants/api";
import { Typography } from "ui-kit/Typography";
import { Routes } from "constants/routes";
import singleImg from "./single.png";
import epImg from "./ep.png";
import albumImg from "./album.png";
import musicVideoImg from "./musicVideo.png";
import tiktokImg from "./tiktok.png";
import ringtonImg from "./rington.png";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { useRelease } from "../hooks/useRelease";
import { setDraftSaveGuard } from "../draftSaveGuard";

enum ReleaseType {
  Single = 'single',
  EP = 'ep',
  Album = 'album',
  MusicVideo = 'music_video',
  TikTok = 'tik_tok_sound',
  Ringtone = 'ringtone',
}

const releaseOptions = [
  {
    key: ReleaseType.Single,
    id: 2,
    label: (
      <FormattedMessage
        id="NewRelease.StepType.Single"
        defaultMessage="Single"
      />
    ),
    icon: singleImg,
  },
  {
    key: ReleaseType.EP,
    id: 3,
    label: (
      <FormattedMessage
        id="NewRelease.StepType.EP"
        defaultMessage="EP"
      />
    ),
    icon: epImg,
  },
  {
    key: ReleaseType.Album,
    id: 1,
    label: (
      <FormattedMessage
        id="NewRelease.StepType.Album"
        defaultMessage="Album"
      />
    ),
    icon: albumImg,
  },
  {
    key: ReleaseType.MusicVideo,
    id: 4,
    label: (
      <FormattedMessage
        id="NewRelease.StepType.MusicVideo"
        defaultMessage="Music video"
      />
    ),
    icon: musicVideoImg,
  },
  {
    key: ReleaseType.TikTok,
    id: 7,
    label: (
      <FormattedMessage
        id="NewRelease.StepType.TikTok"
        defaultMessage="Tik Tok sound"
      />
    ),
    icon: tiktokImg,
  },
  {
    key: ReleaseType.Ringtone,
    id: 6,
    label: (
      <FormattedMessage
        id="NewRelease.StepType.Ringtone"
        defaultMessage="Ringtone"
      />
    ),
    icon: ringtonImg,
  },
];

export const StepType: React.FC = () => {
  const [type, setType] = useState<ReleaseType | null>(null);
  const [typeId, setTypeId] = useState<String>()
  const navigate = useNavigate();
  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM);
  const queryParams = new URLSearchParams(location.search);

  const steps = [
    "upload-image",
    "release-info",
    "track-upload",
    "track-info",
    "publish",
    "platform"
  ];
  const { id } = useParams();
  const stepType = queryParams.get("step");
  const { updateRelease, release, fetchRelease } = useRelease();

  useEffect(() => {
    if (!id) return;
    fetchRelease({ backgroundRefresh: true }).catch(() => {
      // useRelease already sets `error`; avoid unhandled promise rejection noise
    });
  }, [id, fetchRelease]);

  const initialTypeId = useMemo(() => {
    const rid = (release as any)?.release_type_id;
    return rid != null ? String(rid) : "";
  }, [release]);

  const isDirty = useCallback(() => {
    if (!id) return false;
    if (!typeId) return false;
    return String(typeId) !== String(initialTypeId);
  }, [id, typeId, initialTypeId]);

  const saveDraft = useCallback(async () => {
    if (!id) return;
    if (!typeId) return;
    await updateRelease({ id, release_type_id: typeId });
  }, [id, typeId, updateRelease]);

  useEffect(() => {
    setDraftSaveGuard({ isDirty, save: saveDraft });
    return () => setDraftSaveGuard(null);
  }, [isDirty, saveDraft]);
  const onNext = async () => {
    try {
      if (!id) {
        const response = await axios.post(
          `${API_BASE_URL}${API_URLS.Releases}`,
          { release_type_id: typeId },
          {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data?.success && response.data?.data) {
          const id = response.data.data.id;
          let nextStepType: string = "";
          let currentStepIndex = steps.findIndex((step) => step === stepType);
          if (currentStepIndex < 0) {
            nextStepType = steps[0];
          } else {
            nextStepType = steps[currentStepIndex + 1];
          }
          if (!nextStepType) {
            console.warn("No next step available.");
            return;
          }

          const query = new URLSearchParams({
            "step": nextStepType,
          }).toString();

          const path = Routes.NewRelease.replace(":id?", id);
          const url = `${path}?${query}`;
          navigate(url);
        }
      } else {
        if (isDirty()) {
          await saveDraft();
        }
        const query = new URLSearchParams({
          "step": steps[0],
        }).toString();
        navigate(`/releases/new-release/${id}?${query}`);

      }
    } catch (e) {
      console.error("Failed to create release:", e);
    }
  };

  const onBack = () => {
    const from = localStorage.getItem("newReleaseFrom");
    localStorage.removeItem("newReleaseFrom");

    if (from === "general") {
      navigate(Routes.General);
    } else {
      navigate(Routes.Releases);
    }
  };
  useEffect(() => {
    const savedType = localStorage.getItem('type');
    if (savedType) {
      setType(savedType as ReleaseType);
    }
  }, []);

  useEffect(() => {
    if (type) {
      localStorage.setItem('type', type);
    }
  },[])
  const intl = useIntl();

  const messages: any = intl.messages;
  
  const rawText = messages["NewRelease.StepType.InfoRingtone"]?.[0]?.value || "";
  const text = rawText.replace(/{br}/g, "\n");
  
  return (
    <Container>
      <Header
        title={
          <FormattedMessage
            id="NewRelease.StepType.Title"
            defaultMessage="Release type (Step 1/6)"
          />
        }
        breadcrumbsText={
          localStorage.getItem("newReleaseFrom") === "general" ? (
            <FormattedMessage
              id="Header.Links.General"
              defaultMessage="General"
            />
          ) : (
            <FormattedMessage
              id="NewRelease.StepType.Breadcrumbs"
              defaultMessage="All releases"
            />
          )
        }
        onBack={onBack}
      />
      <Cards>
        {releaseOptions.map((option) => (
          <Card key={option.key} $image={option.icon} onClick={() => { setType(option.key), setTypeId(String(option.id)) }} $isActive={type === option.key}>
            <LabelPill>
              <StyledTypography variant="body6" color="#fff">
                {option.label}
              </StyledTypography>
            </LabelPill>
          </Card>
        ))}
      </Cards>

      <BottomBar>
        <Typography variant="body4">
          {(!type) && <FormattedMessage
            id="NewRelease.StepType"
            defaultMessage="Select this category if you are releasing one to two tracks that are less than 10 minutes each"
          />}
          {(type === "single") && <FormattedMessage
            id="NewRelease.StepType.Info"
            defaultMessage="Select this category if you are releasing 1 to 2 tracks (musical compositions), each with a duration of less than 10 minutes"
          />}

          {type === "ep" && <FormattedMessage
            id="NewRelease.StepType.InfoEp"
            defaultMessage="Select this category if your release contains 3 to 6 tracks"
          />}
          {type === "album" && <FormattedMessage
            id="NewRelease.StepType.InfoAlbum"
            defaultMessage="Choose this category if your release includes 7 to 12 tracks"
          />}
          {type === "music_video" && <FormattedMessage
            id="NewRelease.StepType.InfoMusic_video"
            defaultMessage="A visual release created to accompany a specific song"
          />}
          {type === "tik_tok_sound" && <FormattedMessage
            id="NewRelease.StepType.tik_tok_sound"
            defaultMessage="TikTok Sound is a separate audio fragment of a track in TikTok with a duration of 5 to 60 seconds, in addition to the main release."
          />}
          {type === "ringtone" && <span style={{ whiteSpace: "pre-line" }}>
            {text}
          </span>
          }
        </Typography>

        <Footer
          nextButtonText={
            <FormattedMessage
              id="NewRelease.StepType.Next"
              defaultMessage="All releases"
            />
          }
          onNext={onNext}
          disabled={!type}
        />
      </BottomBar>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 38px 20px 30px;
  height: max-content;

  /* Mobile: keep action area fixed at the bottom, scroll cards list */
  @media (max-width: 768px) {
    height: 87dvh;
    overflow: hidden;
    position: relative;
  }
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 26px;
  margin: 30px 0;

  @media (max-width: 768px) {
    /* Mobile: 2x3 tile grid */
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin: 16px 0 10px;
    padding: 0;
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const Card = styled.div<{ $isActive?: boolean; $image?: string }>`
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 17px;
  border: 1px solid
    ${({ theme, $isActive }) =>
      $isActive ? theme.palette.border.accent : 'transparent'};

  width: 100%;
  min-height: 128px;
  aspect-ratio: 320 / 128;

  /* Mobile: slightly more compact cards */
  @media (max-width: 768px) {
    padding: 8px;
    border-radius: 14px;
    min-height: unset;
    aspect-ratio: 1 / 1;
    justify-content: flex-start;
  }

  @media (max-width: 375px) {
    border-radius: 12px;
  }

  @media (max-width: 340px) {
    border-radius: 12px;
  }
  background-color: ${({ $isActive, theme }) =>
    $isActive ? theme.palette.background.newReleaseCardsActive : 'transparent'};
    
  ${({ $image }) => $image && `background-image: url(${$image});`}
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  box-shadow: ${({ $isActive }) => ($isActive ? '1px 1px 10px 0px #4971FFEB' : 'none')};

  &:hover {
    cursor: pointer;
    background-color: ${({ theme }) => theme.palette.background.newReleaseCardsActive};
  }

  path {
    fill: ${({ $isActive, theme }) => ($isActive ? theme.palette.icon.accent : 'currentColor')};
  }
`;




const StyledTypography = styled(Typography)``;

const LabelPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
`;

const BottomBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 768px) {
    padding: 12px 0px;
  }
`;
